// Client-side in-memory cache with improved memory management and TTL tracking
const clientCache: {
  posts: Record<string, { data: any[]; timestamp: number }>;
  singlePosts: Record<string, { data: any; timestamp: number }>;
  lastCleanup: number;
  maxEntries: { posts: number; singlePosts: number };
} = {
  posts: {},
  singlePosts: {},
  lastCleanup: Date.now(),
  maxEntries: { posts: 20, singlePosts: 50 }
};

// Cache TTL in milliseconds (5 minutes)
const CLIENT_CACHE_TTL = 5 * 60 * 1000;
// Prefetch threshold (4 minutes)
const PREFETCH_THRESHOLD = 4 * 60 * 1000;
// Cache cleanup interval (10 minutes)
const CACHE_CLEANUP_INTERVAL = 10 * 60 * 1000;

/**
 * Clean up expired or excess cache entries
 */
const cleanupCache = () => {
  const now = Date.now();
  
  // Only run cleanup if it's been a while since last cleanup
  if (now - clientCache.lastCleanup < CACHE_CLEANUP_INTERVAL) {
    return;
  }
  
  clientCache.lastCleanup = now;
  
  // Clean up expired entries
  Object.keys(clientCache.posts).forEach(key => {
    if (now - clientCache.posts[key].timestamp > CLIENT_CACHE_TTL) {
      delete clientCache.posts[key];
    }
  });
  
  Object.keys(clientCache.singlePosts).forEach(key => {
    if (now - clientCache.singlePosts[key].timestamp > CLIENT_CACHE_TTL) {
      delete clientCache.singlePosts[key];
    }
  });
  
  // If still too many entries, remove oldest
  if (Object.keys(clientCache.posts).length > clientCache.maxEntries.posts) {
    const sortedKeys = Object.keys(clientCache.posts)
      .sort((a, b) => clientCache.posts[a].timestamp - clientCache.posts[b].timestamp);
      
    const keysToRemove = sortedKeys.slice(0, sortedKeys.length - clientCache.maxEntries.posts);
    keysToRemove.forEach(key => {
      delete clientCache.posts[key];
    });
  }
  
  if (Object.keys(clientCache.singlePosts).length > clientCache.maxEntries.singlePosts) {
    const sortedKeys = Object.keys(clientCache.singlePosts)
      .sort((a, b) => clientCache.singlePosts[a].timestamp - clientCache.singlePosts[b].timestamp);
      
    const keysToRemove = sortedKeys.slice(0, sortedKeys.length - clientCache.maxEntries.singlePosts);
    keysToRemove.forEach(key => {
      delete clientCache.singlePosts[key];
    });
  }
  
  console.log(`Cache cleanup complete. Current size: posts=${Object.keys(clientCache.posts).length}, singlePosts=${Object.keys(clientCache.singlePosts).length}`);
};

/**
 * Get posts from cache (client or server)
 */
export const getPostsFromCache = <T>(
  cacheKey: string, 
  clientCacheKey?: string
): { posts: T[]; cacheKey: string; clientCacheKey: string; shouldPrefetch: boolean } => {
  const actualClientKey = clientCacheKey || `client_${cacheKey}`;
  let posts: T[] = [];
  let shouldPrefetch = false;
  
  cleanupCache();
  
  // Check client cache first
  if (typeof window !== 'undefined') {
    const clientCached = clientCache.posts[actualClientKey];
    if (clientCached) {
      const age = Date.now() - clientCached.timestamp;
      
      if (age < CLIENT_CACHE_TTL) {
        posts = clientCached.data as T[];
        
        // Check if we should prefetch in the background (cache is getting old)
        shouldPrefetch = age > PREFETCH_THRESHOLD;
        
        console.log(`Cache hit for "${actualClientKey}" (client), age: ${Math.round(age/1000)}s, prefetch: ${shouldPrefetch}`);
      } else {
        console.log(`Cache expired for "${actualClientKey}" (client), age: ${Math.round(age/1000)}s`);
        // Clean up expired cache entries
        delete clientCache.posts[actualClientKey];
      }
    }
  }
  
  return { posts, cacheKey, clientCacheKey: actualClientKey, shouldPrefetch };
};

/**
 * Save posts to caches with memory management
 */
export const savePostsToCaches = <T>(cacheKey: string, clientCacheKey: string, data: T[]): void => {
  // Save to client cache
  if (typeof window !== 'undefined') {
    const actualClientKey = clientCacheKey || `client_${cacheKey}`;
    
    clientCache.posts[actualClientKey] = {
      data,
      timestamp: Date.now()
    };
    
    console.log(`Saved to client cache: ${actualClientKey}`);
    
    // Run cleanup to prevent memory bloat
    cleanupCache();
  }
};

/**
 * Get single post from caches
 */
export const getSinglePostFromCaches = <T>(
  slug: string
): { post: T | null; shouldPrefetch: boolean } => {
  let post: T | null = null;
  let shouldPrefetch = false;
  
  // Check client cache
  if (typeof window !== 'undefined') {
    const clientCached = clientCache.singlePosts[slug];
    if (clientCached) {
      const age = Date.now() - clientCached.timestamp;
      
      if (age < CLIENT_CACHE_TTL) {
        post = clientCached.data as T;
        
        // Check if we should prefetch in the background (cache is getting old)
        shouldPrefetch = age > PREFETCH_THRESHOLD;
        
        console.log(`Cache hit for post "${slug}" (client), age: ${Math.round(age/1000)}s, prefetch: ${shouldPrefetch}`);
      } else {
        console.log(`Cache expired for post "${slug}" (client), age: ${Math.round(age/1000)}s`);
        // Clean up expired cache entries
        delete clientCache.singlePosts[slug];
      }
    }
  }
  
  return { post, shouldPrefetch };
};

/**
 * Save single post to caches
 */
export const saveSinglePostToCaches = <T>(slug: string, data: T | null): void => {
  // Save to client cache
  if (typeof window !== 'undefined') {
    clientCache.singlePosts[slug] = {
      data,
      timestamp: Date.now()
    };
    
    console.log(`Saved post to client cache: ${slug}`);
  }
};

/**
 * Clear client cache
 */
export const clearClientCache = (): void => {
  // Clear client cache
  if (typeof window !== 'undefined') {
    clientCache.posts = {};
    clientCache.singlePosts = {};
    console.log('Client cache cleared');
  }
};

/**
 * Invalidate specific client cache key
 */
export const invalidateClientCacheKey = (key: string): void => {
  if (typeof window !== 'undefined' && clientCache.posts[key]) {
    delete clientCache.posts[key];
    console.log(`Client cache invalidated: ${key}`);
  }
};

/**
 * Invalidate all posts cache keys matching a pattern
 */
export const invalidatePostsCacheByPattern = (pattern: string): void => {
  if (typeof window !== 'undefined' && pattern) {
    let invalidatedCount = 0;
    
    Object.keys(clientCache.posts).forEach(key => {
      if (key.includes(pattern)) {
        delete clientCache.posts[key];
        invalidatedCount++;
      }
    });
    
    if (invalidatedCount > 0) {
      console.log(`Invalidated ${invalidatedCount} cache entries matching pattern: ${pattern}`);
    }
  }
};

/**
 * Invalidate post cache by slug
 */
export const invalidatePostCache = (slug: string): void => {
  if (typeof window !== 'undefined' && clientCache.singlePosts[slug]) {
    delete clientCache.singlePosts[slug];
    console.log(`Post cache invalidated: ${slug}`);
  }
};

/**
 * Clear all cache entries related to blog posts search or categories
 */
export const clearBlogFiltersCache = (): void => {
  if (typeof window !== 'undefined') {
    let clearedCount = 0;
    
    // First clear any category-specific caches
    Object.keys(clientCache.posts).forEach(key => {
      if (key.includes('_category_') || key.includes('blog_')) {
        delete clientCache.posts[key];
        clearedCount++;
      }
    });
    
    // Then clear search caches
    Object.keys(clientCache.posts).forEach(key => {
      if (key.includes('_search_')) {
        delete clientCache.posts[key];
        clearedCount++;
      }
    });
    
    if (clearedCount > 0) {
      console.log(`Cleared ${clearedCount} blog filter cache entries`);
    }
  }
};

/**
 * Force clear all cache entries
 */
export const forceFullCacheClear = (): void => {
  if (typeof window !== 'undefined') {
    const totalEntries = Object.keys(clientCache.posts).length + Object.keys(clientCache.singlePosts).length;
    
    // Clear all caches
    clientCache.posts = {};
    clientCache.singlePosts = {};
    
    console.log(`Force cleared all ${totalEntries} cache entries`);
  }
};
